import "@hotwired/turbo-rails";
import $ from 'jquery';
import 'regenerator-runtime/runtime';
import 'trix';

import '@fortawesome/fontawesome-svg-core/styles.css';
import '@rails/actiontext';
import select2 from 'select2';
import 'tippy.js/animations/shift-toward.css';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
select2(); // necessary to init select2

import * as bootstrap from 'bootstrap';
import numeral from 'numeral';
import Sortable from 'sortablejs';

// Rails imports
import Appsignal from "@appsignal/javascript";
import { plugin as consolePlugin } from "@appsignal/plugin-breadcrumbs-console";
import { plugin as networkPlugin } from "@appsignal/plugin-breadcrumbs-network";
import { plugin as pathPlugin } from "@appsignal/plugin-path-decorator";
import { installErrorHandler } from "@appsignal/stimulus";
import { config,dom,library } from '@fortawesome/fontawesome-svg-core';
import { Application } from '@hotwired/stimulus';
import * as ActiveStorage from "@rails/activestorage";
import Rails from '@rails/ujs';
import mixpanel from 'mixpanel-browser';
import { registerControllers } from 'stimulus-vite-helpers';

// local imports
import { icons } from '@/fontawesome';
import Utilities from '@/utils/utilities';

library.add(...icons)
config.mutateApproach = 'sync'

dom.watch()

// Uncomment to copy all static images under ../images to the output folder and reference
// them with the vite_image_tag helper in views (e.g <%= vite_image_tag 'rails.png' %>)
// or the imagePath JavaScript helper below.
const images = import.meta.glob('./**/images');

// Setup global windows
window.jQuery = window.$ = $
window.Controllers = {};
window.App = {}
window.App.Utilities = Utilities
window.bootstrap = bootstrap;
window.numeral = numeral;
window.Sortable = Sortable;
window.App.mixpanelInitialized = false;
window.App.mixpanelConfig = {};

// Rails-related
if (!window.Rails) { Rails.start() } // hack for now until we move off of UJS in favor of Turbo
ActiveStorage.start()


// AppSignal error tracking / breadcrumbs

// These keys are okay to expose to public according to AppSignal

const appsignal = process.env.productionMode === 'true'
                ? new Appsignal({ key: "cb9c2ecb-b9d8-42ae-817b-a2bbf6c94f85" })
                : new Appsignal({ key: "5e6a4845-f426-4374-9bf6-3baee51c00ad" })
appsignal.use(consolePlugin())
appsignal.use(networkPlugin())
appsignal.use(pathPlugin())

const application = Application.start()
installErrorHandler(appsignal, application) // line needs to be before registerControllers

// Import Stimulus controllers globally
const controllers = import.meta.glob('@/**/*_controller.{coffee,js}', { eager: true })
registerControllers(application, controllers)

// DebugMode - why we need this?
App.DebugMode = process.env.developmentMode === 'true';

// Disable console warnings unless development
if (!App.DebugMode) {
  console.warn = function() {};
}

App.log = function(message) {
  if (App.DebugMode) {
    console.log("BK360: " + message);
  }
};

App.normalizePath = function(path) {
  try {
    return path
      .split('/')
      .map(segment => {
        // Replace numeric IDs
        if (/^\d+$/.test(segment)) return ':id';
        // Replace UUIDs
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':id';
        return segment;
      })
      .join('/');
  } catch (e) {
    return path;
  }
};

App.configureMixpanel = function() {
  App.mixpanelConfig = App.mixpanelConfig || {};

  const tokenMeta = document.querySelector('meta[name="mixpanel-token"]');
  App.mixpanelConfig.token = tokenMeta ? tokenMeta.content : '';

  const userIdMeta = document.querySelector('meta[name="mixpanel-user-id"]');
  App.mixpanelConfig.userId = userIdMeta ? userIdMeta.content : '';

  const userEmailMeta = document.querySelector('meta[name="mixpanel-user-email"]');
  App.mixpanelConfig.userEmail = userEmailMeta ? userEmailMeta.content : '';

  const userNameMeta = document.querySelector('meta[name="mixpanel-user-name"]');
  App.mixpanelConfig.userName = userNameMeta ? userNameMeta.content : '';

  const impersonatingMeta = document.querySelector('meta[name="mixpanel-impersonator-id"]');
  App.mixpanelConfig.impersonatorId = impersonatingMeta ? impersonatingMeta.content : null;

  const railsEnvMeta = document.querySelector('meta[name="mixpanel-rails-env"]');
  App.mixpanelConfig.isDevelopment = railsEnvMeta ? railsEnvMeta.content === 'development' : false;
}

App.setMixpanelPeopleDetails = function() {
  if (!App.mixpanelConfig.userId || App.mixpanelConfig.userId.length === 0) {
    return;
  }

  // Used for a cache key
  const currentDetails = {
    id: App.mixpanelConfig.userId,
    email: App.mixpanelConfig.userEmail,
    name: App.mixpanelConfig.userName,
    impersonatorId: App.mixpanelConfig.impersonatorId
  };

  const currentDetailsString = JSON.stringify(currentDetails);

  const storedDetailsString = localStorage.getItem('bk360_mixpanel_people_details');

  // Only set people info if details have changed. This avoids sending additional
  // API calls to Mixpanel.
  if (currentDetailsString !== storedDetailsString) {
    if (!App.mixpanelInitialized) {
      App.log('Skip setting people details because mixpanel is not initialized');
      return;
    }
    App.log('Setting MP people details: ' + JSON.stringify(App.mixpanelConfig));
    mixpanel.identify(App.mixpanelConfig.userId);

    // NOTE: Avoid anything sensitive here in case the machine is used in a shared
    // environment.
    mixpanel.people.set({
      '$email': App.mixpanelConfig.userEmail,
      name: App.mixpanelConfig.userName
    });

    localStorage.setItem('bk360_mixpanel_people_details', currentDetailsString);
  }
};


numeral.register('locale', 'us', {
  delimiters: {
    thousands: ',',
    decimal: '.'
  },
  abbreviations: {
    thousand: 'K',
    million: 'M',
    billion: 'B',
    trillion: 'T'
  },
  currency: { symbol: '$' }
});

numeral.locale('us');

// TODO: Remove these global events :[
document.addEventListener('turbo:load', function() {
  // Reload mixpanel config to pick up any changes, mainly impersonation.
  App.configureMixpanel();
  App.setMixpanelPeopleDetails();

  // Track the page visit after identity is set
  if (App.mixpanelInitialized) {
    mixpanel.track(`Visited - ${App.normalizePath(window.location.pathname)}`, {
      path: window.location.href,
      action: 'client',
      user_agent: navigator.userAgent,
      turbo: true,
      impersonatorId: App.mixpanelConfig.impersonatorId
    });
  }

  // set app variables from body data
  Object.entries(document.body.dataset).forEach(([k, v]) => {
    App[k] = v;
    if (App.DebugMode) {
      App.log("Setting - " + k + ": " + v);
    }
  });

  setTimeout(function() {
    document.body.classList.remove('preload', 'no-scroll');
    document.documentElement.classList.remove('no-scroll');
  }, 100);

  document.querySelectorAll('[data-numeral]').forEach((element) => {
    const value = element.innerHTML;
    element.innerHTML = numeral(value).format(element.dataset.numeral);
    if (element.dataset.tooltip !== undefined) {
      element.dataset.tippyContent = numeral(value).format('$0,0');
      element.dataset.tippyPlacement = 'top';
    }
  });

});

window.addEventListener('resize', function() {
  document.body.classList.add('preload');
  clearTimeout(window.resizeTimer);
  window.resizeTimer = setTimeout(function() {
    document.body.classList.remove('preload');
  }, 500);
});

document.addEventListener('turbo:frame-render', (event) => {
  if (event.target.id === 'offcanvas') {
    if (event?.target?.src) {
      const url = new URL(event.target.src);
      const path = url.pathname;
      if (App.mixpanelInitialized) {
        mixpanel.track(`Visited - ${App.normalizePath(path)}`, { path: url.href, action: 'client', user_agent: navigator.userAgent, turbo: true, impersonatorId: App.mixpanelConfig.impersonatorId });
      } else {
        App.log('Skip tracking because mixpanel is not initialized');
      }
    }
  }
});

// Original from the transfer_to_vite folder

import ChartStyles from '@/utils/chart_styles';
import Chart from 'chart.js/auto';
import AnnotationPlugin from 'chartjs-plugin-annotation';
// This chart doesn't currently support Chart.js v4, so we've brough the code
// in to this repo and are importing it directly. Once, it has been updated,
// we can remove this and import it from npm.
// See: https://github.com/ciprianciurea/chartjs-plugin-doughnutlabel/issues
import DoughnutLablesPlugin from '@/utils/doughnut_label_plugin';
import CrosshairPlugin from '@/utils/crosshair_plugin';

Chart.defaults.font.family = 'Montserrat';
Chart.defaults.color = ChartStyles.colors.blueGray;
Chart.defaults.font.weight = '600'

Chart.register(AnnotationPlugin);
Chart.register(CrosshairPlugin);
Chart.register(DoughnutLablesPlugin);

Turbo.StreamActions.redirect = function() {
  Turbo.visit(this.target);
};

Turbo.StreamActions.remove_empty_day_group = function() {
  const dayGroup = document.getElementById(this.target)
  if (!dayGroup) return

  if (dayGroup.querySelectorAll('.meetings-meeting-card').length === 0) {
    dayGroup.remove()
  }
}

Turbo.StreamActions.close_modal = function() {
  const targetId = this.target || 'updated-modal'
  const element = document.getElementById(targetId)
  if (!element) return

  // Try to get existing Bootstrap modal instance
  let modal = bootstrap.Modal.getInstance(element)

  // If no instance, try to find modal within the element (for turbo-frame containers)
  if (!modal) {
    const modalElement = element.querySelector('.modal')
    if (modalElement) {
      modal = bootstrap.Modal.getInstance(modalElement)
    }
  }

  if (modal) {
    modal.hide()
  }
}

Turbo.StreamActions.close_offcanvas = function() {
  const element = document.getElementById('updated-offcanvas')
  const offcanvas = bootstrap.Offcanvas.getInstance(element)
  offcanvas.hide()
}

Turbo.StreamActions.open_offcanvas = function() {
  // Get the target from the turbo-stream action (defaults to 'updated-offcanvas')
  const targetId = this.getAttribute('target') || this.target || 'updated-offcanvas'
  const element = document.getElementById(targetId)

  if (!element) {
    console.warn('Offcanvas element not found:', targetId)
    return
  }

  let offcanvas = bootstrap.Offcanvas.getInstance(element)
  if (!offcanvas) {
    offcanvas = new bootstrap.Offcanvas(element)
  }
  offcanvas.show()
}

Turbo.StreamActions.close_hovercard = function() {
  const hovercards = document.querySelectorAll('[data-controller="hovercard"]')

  hovercards.forEach(hover => {
    const controller = application.getControllerForElementAndIdentifier(
      hover,
      'hovercard'
    )

    if (controller) { controller.hide() }
  })
}

Turbo.StreamActions.remove_element = function() {
  const elementId = this.getAttribute('element-id') || this.target
  const element = document.getElementById(elementId)
  if (element) {
    element.remove()
  }
}

// Replace Turbo's native window.confirm (used by every `data-turbo-confirm`)
// with a styled Bootstrap modal so delete/confirm prompts match the rest of
// the app instead of a raw browser dialog.
function appConfirmModalElement() {
  let el = document.getElementById('app-confirm-modal')
  if (el) return el

  el = document.createElement('div')
  el.id = 'app-confirm-modal'
  el.className = 'modal fade'
  el.tabIndex = -1
  el.innerHTML = `
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Please confirm</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="d-flex align-items-center gap-2 mb-2 d-none" data-confirm-subject>
            <span data-confirm-avatar></span>
            <span class="fw-semibold" data-confirm-name></span>
          </div>
          <div data-confirm-message></div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" data-confirm-ok>Confirm</button>
        </div>
      </div>
    </div>`
  document.body.appendChild(el)
  return el
}

Turbo.config.forms.confirm = function(message, formElement, submitter) {
  if (!window.bootstrap) return window.confirm(message)

  const el = appConfirmModalElement()
  el.querySelector('[data-confirm-message]').textContent = message

  // Optional subject (avatar + name) — read from the submitter (button_to puts
  // data attributes there) and fall back to the form element.
  const source = submitter || formElement
  const name = source?.dataset?.confirmName
  const avatarHtml = source?.dataset?.confirmAvatarHtml
  const subject = el.querySelector('[data-confirm-subject]')
  if (name || avatarHtml) {
    el.querySelector('[data-confirm-avatar]').innerHTML = avatarHtml || ''
    el.querySelector('[data-confirm-name]').textContent = name || ''
    subject.classList.remove('d-none')
  } else {
    subject.classList.add('d-none')
  }

  const okButton = el.querySelector('[data-confirm-ok]')
  const modal = bootstrap.Modal.getOrCreateInstance(el)

  return new Promise((resolve) => {
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      okButton.removeEventListener('click', onOk)
      el.removeEventListener('hidden.bs.modal', onHide)
      resolve(value)
    }
    const onOk = () => { finish(true); modal.hide() }
    const onHide = () => finish(false)

    okButton.addEventListener('click', onOk)
    el.addEventListener('hidden.bs.modal', onHide)
    modal.show()
    okButton.focus()
  })
}

Turbo.StreamActions.scroll_to = function() {
  const target = document.getElementById(this.target)
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

document.addEventListener('DOMContentLoaded', function() {
  App.configureMixpanel();

  if(App.mixpanelConfig.token) {
    mixpanel.init(App.mixpanelConfig.token, {
      persistence: 'localStorage',
      track_pageview: false,
      debug: App.mixpanelConfig.isDevelopment,
    });

    App.setMixpanelPeopleDetails();
    App.mixpanelInitialized = true;
  }
});

// Mixpanel tracking
function handleMixpanelTracking(event) {
  if (!window.App.mixpanelInitialized) {
    console.log('Skip tracking because mixpanel is not initialized')
    return
  }

  // Check if the event has a data-mixpanel-event attribute that's they key to tracking
  const trackElement = event.target.closest('[data-mixpanel-event]')
  if (!trackElement) return

  const eventName = trackElement.dataset.mixpanelEvent
  if (!eventName) {
    console.log('No mixpanel event name provided')
    return
  }

  let properties = {
    user_id: window.App.mixpanelConfig?.userId,
    impersonator_id: window.App.mixpanelConfig?.impersonatorId
  }

  // Add any additional properties from data-mixpanel-properties
  const additionalProperties = trackElement.dataset.mixpanelProperties
  if (additionalProperties) {
    try {
      properties = { ...properties, ...JSON.parse(additionalProperties) }
    } catch (e) {
      console.log('Error parsing mixpanel properties: ' + e.message)
    }
  }

  mixpanel.track(eventName, properties)
}

document.addEventListener('click', handleMixpanelTracking)
document.addEventListener('submit', handleMixpanelTracking)
